import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart3,
  Users,
  Briefcase,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Calendar,
  ArrowRight,
  LogOut,
  RefreshCw,
  PieChart,
  Activity,
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_NAMES = {
  cases: 'القضايا',
  execution: 'التنفيذ',
  reviews: 'المراجعات',
  internal: 'المهام الداخلية',
};

const STATUS_NAMES = {
  pending: 'قيد الانتظار',
  in_progress: 'قيد العمل',
  completed: 'مكتمل',
  archived: 'مؤرشف',
};

const PRIORITY_NAMES = {
  low: 'منخفضة',
  normal: 'عادية',
  high: 'عالية',
  urgent: 'عاجلة',
};

export default function TaskReports() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [employeeReports, setEmployeeReports] = useState([]);
  const [workflowReport, setWorkflowReport] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
    }
    fetchReports();
  }, [navigate]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [summaryRes, employeesRes, workflowRes] = await Promise.all([
        axios.get(`${API}/tasks/reports/summary`, { headers }),
        axios.get(`${API}/tasks/reports/employees`, { headers }),
        axios.get(`${API}/tasks/reports/workflow`, { headers }),
      ]);
      
      setSummary(summaryRes.data);
      setEmployeeReports(employeesRes.data);
      setWorkflowReport(workflowRes.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir="rtl">
        <div className="animate-spin w-12 h-12 border-4 border-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div>
              <h1 className="text-xl font-heading font-bold text-white">تقارير المهام</h1>
              <p className="text-sm text-gray-300 font-body">تحليل الأداء وسير العمل</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchReports}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-md hover:bg-white/20 transition-all"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              تحديث
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-gold/20 text-white rounded-md hover:bg-gold/30 transition-all"
              data-testid="back-to-dashboard"
            >
              <ArrowRight className="w-4 h-4" />
              لوحة التحكم
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'summary', label: 'ملخص عام', icon: PieChart },
            { id: 'employees', label: 'أداء الموظفين', icon: Users },
            { id: 'workflow', label: 'سير العمل', icon: Activity },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-body transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-white' : 'text-gray-400'}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Summary Tab */}
        {activeTab === 'summary' && summary && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="إجمالي المهام"
                value={summary.total}
                icon={Briefcase}
                color="bg-blue-500"
              />
              <StatCard
                title="قيد الانتظار"
                value={summary.pending}
                icon={Clock}
                color="bg-yellow-500"
              />
              <StatCard
                title="قيد العمل"
                value={summary.in_progress}
                icon={AlertCircle}
                color="bg-orange-500"
              />
              <StatCard
                title="مكتمل"
                value={summary.completed}
                icon={CheckCircle}
                color="bg-green-500"
              />
            </div>

            {/* Charts Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* By Category */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  المهام حسب الفئة
                </h3>
                <div className="space-y-4">
                  {Object.entries(summary.by_category).map(([cat, count]) => (
                    <div key={cat}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-body">{CATEGORY_NAMES[cat] || cat}</span>
                        <span className="text-sm font-bold">{count}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${(count / Math.max(1, summary.total - summary.archived)) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Priority */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  المهام حسب الأولوية
                </h3>
                <div className="space-y-4">
                  {Object.entries(summary.by_priority).map(([priority, count]) => {
                    const colors = {
                      low: 'bg-gray-400',
                      normal: 'bg-blue-400',
                      high: 'bg-orange-400',
                      urgent: 'bg-red-500',
                    };
                    return (
                      <div key={priority}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-body">{PRIORITY_NAMES[priority] || priority}</span>
                          <span className="text-sm font-bold">{count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${colors[priority] || 'bg-gray-400'} rounded-full transition-all`}
                            style={{ width: `${(count / Math.max(1, summary.total - summary.archived)) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-heading font-bold text-primary flex items-center gap-2">
                <Users className="w-5 h-5" />
                أداء الموظفين
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">الموظف</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">إجمالي المهام</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">مكتملة</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">قيد العمل</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">معلقة</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">نسبة الإنجاز</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-600">متوسط الأيام</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {employeeReports.map((emp) => (
                    <tr key={emp.employee_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium">{emp.employee_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-600">{emp.total_tasks}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-green-600 font-medium">{emp.completed_tasks}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-blue-600">{emp.in_progress_tasks}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-yellow-600">{emp.pending_tasks}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${emp.completion_rate}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-green-600">{emp.completion_rate}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-600">{emp.average_completion_days} يوم</span>
                      </td>
                    </tr>
                  ))}
                  {employeeReports.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                        لا توجد بيانات
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Workflow Tab */}
        {activeTab === 'workflow' && workflowReport && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {/* By Category */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-primary mb-4">حسب الفئة</h3>
                <div className="space-y-3">
                  {Object.entries(workflowReport.by_category).map(([cat, count]) => (
                    <div key={cat} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="font-body">{CATEGORY_NAMES[cat] || cat}</span>
                      <span className="font-bold text-primary">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Status */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-primary mb-4">حسب الحالة</h3>
                <div className="space-y-3">
                  {Object.entries(workflowReport.by_status).map(([status, count]) => {
                    const colors = {
                      pending: 'bg-yellow-50 text-yellow-700',
                      in_progress: 'bg-blue-50 text-blue-700',
                      completed: 'bg-green-50 text-green-700',
                      archived: 'bg-gray-50 text-gray-700',
                    };
                    return (
                      <div key={status} className={`flex justify-between items-center p-3 rounded-lg ${colors[status] || 'bg-gray-50'}`}>
                        <span className="font-body">{STATUS_NAMES[status] || status}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Monthly Trend */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-primary mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  حسب الشهر
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {Object.entries(workflowReport.by_month).map(([month, count]) => (
                    <div key={month} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="font-body text-sm">{month}</span>
                      <span className="font-bold text-primary">{count}</span>
                    </div>
                  ))}
                  {Object.keys(workflowReport.by_month).length === 0 && (
                    <p className="text-center text-gray-400">لا توجد بيانات</p>
                  )}
                </div>
              </div>
            </div>

            {/* Total Summary */}
            <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-1">إجمالي المهام</h3>
                  <p className="text-white/80">جميع المهام في النظام</p>
                </div>
                <div className="text-4xl font-bold">{workflowReport.total_tasks}</div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// مكون بطاقة الإحصائية
function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-body mb-1">{title}</p>
          <p className="text-3xl font-bold text-primary">{value}</p>
        </div>
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}
